import { createMenuRouteRecords } from "./menuRoute";

/**
 * Resolves a backend menu URL to an existing lazy view component.
 *
 * Only files under `views` can be resolved. Both `path.vue` and
 * `path/index.vue` are supported. A missing file or stale split chunk falls
 * back to the existing 404 component instead of leaving a dynamic-import
 * error during navigation.
 */
function createMenuViewResolver(notFoundComponent) {
  const views = require.context("../views", true, /\.vue$/, "lazy");
  const viewKeys = views.keys();

  function getViewKey(routePath) {
    if (typeof routePath !== "string") {
      return null;
    }

    const relativePath = routePath
      .trim()
      .replace(/^\/+/, "")
      .replace(/\/+$/, "");
    const segments = relativePath ? relativePath.split("/") : [];

    if (
      !segments.length ||
      segments.some(function(segment) {
        return !segment || segment === "." || segment === "..";
      })
    ) {
      return null;
    }

    const basePath = relativePath.replace(/\.vue$/, "").replace(/\/+$/, "");
    const directKey = "./" + basePath + ".vue";
    const indexKey = "./" + basePath + "/index.vue";

    if (viewKeys.indexOf(directKey) !== -1) {
      return directKey;
    }

    return viewKeys.indexOf(indexKey) === -1 ? null : indexKey;
  }

  return function resolveMenuView(routePath) {
    const viewKey = getViewKey(routePath);

    if (!viewKey) {
      return notFoundComponent;
    }

    return function() {
      return views(viewKey)
        .then(function(module) {
          return module.default || module;
        })
        .catch(function() {
          return notFoundComponent;
        });
    };
  };
}

/**
 * Owns the dynamic matcher lifecycle without replacing the exported Router
 * instance. Keeping that instance preserves the Vue application, its guards,
 * and the auto-update listener registered by routeGuard.
 */
export function createDynamicRouteManager(router, options) {
  const config = options || {};
  const constantRoutes = Array.isArray(config.constantRoutes)
    ? config.constantRoutes
    : [];
  const resolveMenuView = createMenuViewResolver(config.notFoundComponent);
  let dynamicRoutesLoaded = false;

  function replaceRouterMatcher(routeRecords) {
    const freshRouter = config.createRouter(routeRecords);

    router.matcher = freshRouter.matcher;
    router.options.routes = routeRecords;
  }

  function installDynamicRoutes(menuTree) {
    const dynamicRoutes = createMenuRouteRecords(menuTree, {
      layoutComponent: config.layoutComponent,
      routeViewComponent: config.routeViewComponent,
      resolvePageComponent: resolveMenuView,
      layoutPath: "/Layout",
      containerPathPrefix: "/__menu",
    });

    /*
     * Vue Router 3 keeps the first registered record for duplicate paths.
     * Menu records must precede static records so backend button permissions
     * are retained, while legacy static detail routes remain available.
     */
    replaceRouterMatcher(dynamicRoutes.concat(constantRoutes));
    dynamicRoutesLoaded = true;
  }

  function resetDynamicRoutes() {
    replaceRouterMatcher(constantRoutes);
    dynamicRoutesLoaded = false;
  }

  return {
    installDynamicRoutes: installDynamicRoutes,
    resetDynamicRoutes: resetDynamicRoutes,
    isDynamicRoutesLoaded: function() {
      return dynamicRoutesLoaded;
    },
  };
}

/**
 * Re-enters the current URL after dynamic records are installed. `path`
 * already contains dynamic params; query and hash must be retained as-is.
 */
export function createRematchLocation(to) {
  return {
    path: to.path,
    query: to.query,
    hash: to.hash,
    replace: true,
  };
}

export function createErrorLocation(code) {
  const errorPath = code === 403 ? "/403" : code === 500 ? "/500" : "/404";
  return { path: errorPath, replace: true };
}

/**
 * Keeps the existing Vue Router 3 duplicate-navigation compatibility behavior.
 * It intentionally preserves the project's current handling of all rejected
 * push promises; tightening that policy would be a separate behavior change.
 */
export function installSafeRouterPush(router) {
  const RouterConstructor = router.constructor;

  if (RouterConstructor.__safePushInstalled) {
    return;
  }

  const routerPush = RouterConstructor.prototype.push;
  RouterConstructor.prototype.push = function push(location) {
    return routerPush.call(this, location).catch((error) => error);
  };
  RouterConstructor.__safePushInstalled = true;
}

export default {
  createDynamicRouteManager: createDynamicRouteManager,
  createRematchLocation: createRematchLocation,
  createErrorLocation: createErrorLocation,
  installSafeRouterPush: installSafeRouterPush,
};
